'use client';

import { useEffect } from 'react';

import { Sheet, SheetClose, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { Appointment } from '@/types/appointments.types';
import { Link } from '@/components/ui/link/link.component';
import { Button } from '@/components/ui/button/button.component';
import { ModalCloseButton } from '@/components/ui/modal-close-button/modal-close-button.component';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface CardAppointmentCancelRequestModalProps {
  appointment: Appointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditPatient?: () => void;
  onEditDate?: () => void;
}

export function CardAppointmentCancelRequestModal({
  appointment,
  open,
  onOpenChange,
  onEditPatient,
  onEditDate,
}: CardAppointmentCancelRequestModalProps) {
  const analytics = getAnalyticsPlugin();

  useEffect(() => {
    if (open) {
      analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CANCEL_MODAL_VIEWED);
    }
  }, [open, analytics]);

  const handleChangeDateTime = () => {
    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CLICK_CHANGE_DATETIME);
    onOpenChange(false);
    if (onEditDate) {
      onEditDate();
    }
  };

  const handleChangePatient = () => {
    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CLICK_CHANGE_PATIENT);
    onOpenChange(false);
    if (onEditPatient) {
      onEditPatient();
    }
  };

  const handleCancelClick = () => {
    analytics.track(AnalyticsEnum.MY_APPOINTMENTS_CLICK_CANCEL_APPOINTMENT);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="centerFit"
        className="p-0"
        hasHeader
      >
        <div className="flex flex-col">
          <div className="flex justify-between items-start gap-2 px-6 pt-5 pb-4">
            <SheetTitle>
              <Typography
                variant={TypographyVariant.MODAL_TITLE}
                className="leading-8"
              >
                Deseja cancelar o agendamento?
              </Typography>
            </SheetTitle>

            <SheetClose asChild className="shrink-0">
              <ModalCloseButton skipInitialFocus label="Fechar modal" />
            </SheetClose>
          </div>

          <div className="flex flex-col gap-6 px-6 pb-5">
            <Typography
              variant={TypographyVariant.BODY_TEXT}
              className="leading-6"
            >
              Você pode alterar o agendamento sem precisar cancelar. O que deseja fazer?
            </Typography>

            <div className="flex flex-col gap-4">
              {onEditDate && (
                <Button
                  onClick={handleChangeDateTime}
                  variant="default"
                >
                  Alterar data e horário
                </Button>
              )}

              {onEditPatient && (
                <Button
                  onClick={handleChangePatient}
                  variant={onEditDate ? 'secondary' : 'default'}
                >
                  Trocar paciente
                </Button>
              )}

              <Button
                variant="secondary"
                asChild
                onClick={handleCancelClick}
              >
                <Link href={`/agendamentos/${appointment.id}/solicitar-cancelamento`}>
                  Cancelar agendamento
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
